import { calculateWorkoutZoneSeconds } from '../telemetry/index.js';

/**
 * Calculates a 1-10 subjective/heart-rate effort score for a workout.
 * @param {Object} w - Workout object
 * @returns {number} Effort score between 1 and 10
 */
export function getWorkoutEffortScore(w) {
  if (!w) return 5;
  if (w.effortScore !== undefined && w.effortScore !== null) {
    const ef = Number(w.effortScore);
    if (!isNaN(ef)) return Math.min(10, Math.max(1, ef));
  }
  if (w.avgHeartrate) {
    const hr = Number(w.avgHeartrate);
    if (!isNaN(hr) && hr > 0) {
      const userMaxHr = 168;
      return Math.min(10, Math.max(1, Math.round((hr / userMaxHr) * 10)));
    }
  }
  // Fallback defaults by activity type / name
  const type = (w.type || '').toLowerCase();
  const name = (w.name || '').toLowerCase();
  if (name.includes('race') || type.includes('race')) return 9;
  if (type.includes('hyrox')) return 8;
  if (type.includes('run')) return 7;
  if (type.includes('cycle') || type.includes('swim')) return 6;
  if (type.includes('strength') || type.includes('weight')) return 6;
  if (type.includes('yoga') || type.includes('stretch')) return 3;
  if (type.includes('walk')) return 2;
  return 5; // standard fallback
}

/**
 * Calculates the comprehensive suffer score / pulse load for a workout.
 * @param {Object} w - Workout object
 * @param {Function} [zoneSecFn=calculateWorkoutZoneSeconds] - Heart rate zone calculation function
 * @returns {number} Suffer score
 */
export function getWorkoutSufferScore(w, zoneSecFn = calculateWorkoutZoneSeconds) {
  if (!w || w.excludeFromStats) return 0;
  const mult = Number(w.sufferMultiplier);
  const validMult = !isNaN(mult) && mult > 0 ? mult : 1;

  if (w.sufferScore !== null && w.sufferScore !== undefined) {
    const ss = Number(w.sufferScore);
    if (!isNaN(ss) && ss > 0) {
      return Math.round(ss * validMult);
    }
  }
  const zSec = zoneSecFn(w);
  const s = Math.round(
    ((Math.max(0, Number(zSec[0]) || 0)) / 60) * 1.0 +
    ((Math.max(0, Number(zSec[1]) || 0)) / 60) * 2.0 +
    ((Math.max(0, Number(zSec[2]) || 0)) / 60) * 3.0 +
    ((Math.max(0, Number(zSec[3]) || 0)) / 60) * 4.0 +
    ((Math.max(0, Number(zSec[4]) || 0)) / 60) * 5.0
  );
  if (s > 0) return Math.round(s * validMult);

  const duration = Math.max(0, Number(w.duration) || 0);
  const effort = getWorkoutEffortScore(w);
  return Math.round(duration * (effort / 10) * 2.5 * validMult);
}
